/**
 * WarehouseModal — Crear/editar bodega.
 */
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import type { Warehouse } from '../hooks/useInventory'
import { TextField } from '@/components/ds/TextField'
import { Modal } from '@/components/ds/Modal'

interface Props {
  open: boolean
  onClose: () => void
  onSubmit: (data: Partial<Warehouse>) => void
  isPending: boolean
  initial?: Partial<Warehouse> | null
}

export default function WarehouseModal({ open, onClose, onSubmit, isPending, initial }: Props) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<Partial<Warehouse>>()

  useEffect(() => {
    if (open) reset(initial || { is_default: false, is_active: true })
  }, [open, initial, reset])

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [open, onClose])

  if (!open) return null

  return (
    <Modal open onClose={onClose} title={initial?.id ? 'Editar bodega' : 'Nueva bodega'} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            dense
            label="Código"
            required
            placeholder="BOD-01"
            inputProps={register('code', { required: 'El código es obligatorio.' })}
            error={errors.code?.message}
          />
          <TextField
            dense
            label="Nombre"
            required
            placeholder="Bodega Principal"
            inputProps={register('name', { required: 'El nombre es obligatorio.' })}
            error={errors.name?.message}
          />
        </div>

        <TextField
          dense
          label="Dirección"
          placeholder="Av. Principal 123"
          inputProps={register('address')}
        />

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
            <input type="checkbox" {...register('is_default')} className="rounded border-divider-strong" />
            Bodega por defecto
          </label>
          <label className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
            <input type="checkbox" {...register('is_active')} className="rounded border-divider-strong" />
            Activa
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-divider">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60"
          >
            {isPending && <Loader2 size={14} className="animate-spin" />}
            {initial?.id ? 'Guardar cambios' : 'Crear bodega'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
